import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "@/components/z6/ErrorBoundary";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { RICH, useAmbient } from "@/lib/perf";
import { useTrack } from "@/lib/track";
import { Footer, HaraOrb, TopBar } from "@/components/z6/Chrome";
import { GlobeFallback } from "@/components/z6/GlobeFallback";
import { HomeShowcase, ReserveButton, ScrollCue } from "@/components/z6/HeroDish";
import { HeroGlobe } from "@/components/avant/HeroGlobe";

const GlobeCanvas = lazy(() => import("@/components/z6/GlobeCanvas"));
// Editorial headline: masked word reveals, a letter-spaced lead-in, Times Italic on "life"/"live", pink on "life", staggered second line.
const Word = ({ d, className, children, weight }) => (
  <span className="inline-block overflow-hidden pb-[0.14em] align-baseline">
    <motion.span style={weight ? { fontWeight: weight } : undefined} className={`inline-block ${className}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: d, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}>{children}</motion.span>
  </span>
);
const Headline = () => (
  <h1 data-testid="welcome-headline" aria-label="Eat for the life you want to live." className="leading-[0.92] text-[#0B2E44]">
    <span aria-hidden className="flex items-baseline gap-3 sm:gap-4">
      <Word d={0.3} className="font-ui text-[clamp(13px,1.5vw,16px)] uppercase tracking-[0.42em] text-[#33566B]">Eat for the</Word>
      <Word d={0.5} className="font-accent text-[clamp(52px,8vw,88px)] italic text-[#E8508A]">life</Word>
    </span>
    <span aria-hidden className="-mt-1 flex items-baseline gap-2 pl-[clamp(16px,6vw,56px)] sm:gap-3">
      <Word d={0.7} weight={600} className="font-display text-[clamp(32px,4.6vw,52px)]">you want to</Word>
      <Word d={0.9} className="font-accent text-[clamp(42px,6vw,68px)] italic">live.</Word>
    </span>
    <motion.span aria-hidden initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.3, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      className="mt-3 block h-px w-24 origin-left bg-[#E8508A] sm:mt-4" />
  </h1>
);


export default function Welcome() {
  const navigate = useNavigate();
  const [zone, setZone] = useState(null);
  const setAccent = useAmbient((s) => s.setAccent);
  useEffect(() => setAccent("#2DD4BF"), [setAccent]);
  useTrack("home_viewed");
  const card = useRef(null);
  const globe = useRef(null);
  // Stop the globe's render loop while it is scrolled out of view.
  const [offscreen, setOffscreen] = useState(false);
  useEffect(() => {
    if (!globe.current || !window.IntersectionObserver) return undefined;
    const io = new IntersectionObserver(([e]) => setOffscreen(!e.isIntersecting));
    io.observe(globe.current);
    return () => io.disconnect();
  }, []);
  const select = (z) => setZone(z);
  useEffect(() => {
    if (!zone) return undefined;
    const close = (e) => {
      if (card.current?.contains(e.target) || e.target.closest?.("[data-testid^='zone-marker-']")) return;
      setZone(null);
    };
    document.addEventListener("pointerdown", close, { passive: true });
    return () => document.removeEventListener("pointerdown", close);
  }, [zone]);
  return (
    <main data-testid="welcome-page" className="relative min-h-screen overflow-hidden">
      <TopBar />
      <div data-testid="welcome-hero" className="relative z-10 mx-auto flex min-h-[calc(100svh-64px)] max-w-[1400px] flex-col px-5 pt-1 sm:min-h-[calc(100svh-84px)] sm:px-10 landscape:md:px-6">
        <div className="grid flex-1 content-center items-center gap-x-8 gap-y-[clamp(10px,2svh,24px)] landscape:md:gap-x-0 pt-1 landscape:md:grid-cols-[58%_1fr] landscape:md:grid-rows-[auto_auto]">
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            ref={globe} data-testid="home-globe-wrap" className="relative mx-auto aspect-square w-[85vw] portrait:w-[min(85vw,42svh)] portrait:sm:w-[min(85vw,48svh)] landscape:md:row-span-2 landscape:md:-ml-10 landscape:md:w-[calc(100%+40px)] landscape:md:max-w-[calc(100svh-96px)]">
            <HeroGlobe>
            {RICH ? (
              <ErrorBoundary fallback={<GlobeFallback onSelect={select} selectedKey={zone?.key} />}>
                <Suspense fallback={<GlobeFallback onSelect={select} selectedKey={zone?.key} />}>
                  <GlobeCanvas onSelect={select} selectedKey={zone?.key} paused={!!zone} offscreen={offscreen} />
                </Suspense>
              </ErrorBoundary>
            ) : (
              <GlobeFallback onSelect={select} selectedKey={zone?.key} />
            )}
            </HeroGlobe>
            <AnimatePresence>
              {zone && (
                <motion.div key={zone.key} ref={card} data-testid="zone-habit-card" role="dialog" aria-label={zone.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                  className="absolute inset-x-2 bottom-3 z-30 mx-auto max-w-[420px] rounded-2xl bg-white/95 py-4 pl-5 pr-14 ring-1 ring-[#0B5C8A]/15 backdrop-blur-xl">
                  <button type="button" data-testid="zone-habit-close" aria-label="Close" onClick={() => setZone(null)}
                    className="absolute right-1 top-1 flex h-12 w-12 items-center justify-center rounded-full text-[#0B2E44]">
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 6l12 12M18 6L6 18" /></svg>
                  </button>
                  <p className="font-mono text-[15px] uppercase tracking-[0.3em] text-[#0B2E44]">{zone.label}</p>
                  <p data-testid="zone-habit-text" className="mt-1.5 font-display text-xl leading-snug text-[#0B2E44] sm:text-2xl">{zone.habit}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
          <div data-testid="welcome-copy" className="relative z-20 flex flex-col items-start text-left landscape:md:-ml-10 landscape:md:self-end">
            <Headline />
            <motion.p data-testid="welcome-theme-line" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="font-accent mt-2 text-[clamp(16px,2.6vw,19px)] text-[#0B2E44] sm:mt-4">
              Unapologetically indulgent. Uncompromisingly healthy.
            </motion.p>
          </div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.8 }} className="relative z-20 flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4 landscape:md:-ml-10 landscape:md:w-[calc(100%+40px)] landscape:md:flex-col landscape:md:self-start sm:[&>*]:min-w-[260px] [&>*]:flex-1">
            <motion.button whileTap={{ scale: 0.96 }} data-testid="welcome-explore-btn" onClick={() => navigate("/menu")}
              className="z6-btn z6-btn--pink grow sm:min-h-[60px]">
              Explore the <em>menus</em><span aria-hidden className="z6-arrow" />
            </motion.button>
            <motion.button whileTap={{ scale: 0.96 }} data-testid="welcome-hara-btn" onClick={() => navigate("/bioq")}
              className="z6-btn grow bg-white/70 !pl-2 sm:min-h-[60px]">
              <HaraOrb className="hara-orb-sm" />
              Talk to Bio-Q, your waiter
            </motion.button>
            <ReserveButton />
          </motion.div>
        </div>
        <div data-testid="welcome-bottom-row" className="relative z-10 mt-1 justify-center pb-2 max-sm:hidden sm:flex">
          <ScrollCue />
        </div>
      </div>
      <HomeShowcase />
      <Footer orderLinks />
    </main>
  );
}
